<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="incidentName+ ' :: MeritEHS'"/>

            <div class="container d-print-none">
                <h4 class="py-3 mb-4">
                    <ol class="breadcrumb font-weight-bold m-0">
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/incidents`">
                                <i class="fa fa-stethoscope"></i> Incidents
                            </router-link>
                        </li>
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/incidents/view/${incident.IncidentId}`">
                                <incident-name :incident="incident" />
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active">
                            <i class="fa fa-file-pdf-o"></i> PDF
                        </li>
                    </ol>
                </h4>
            </div>
            <div class="container bg-white d-print-none py-4 mb-4">
                <div class="alert alert-info">
                    <strong>How to Export as PDF:</strong>
                    <ol>
                        <li>On your browser, there should be a "File" or "Options" tab with a "Print" option. You may also hold down the "Ctrl" key and press the "P" key.</li>
                        <li>This will bring up the print page options box. There should be an option called "Select Printer" or "Destination".</li>
                        <li>Select "Save as PDF" or "Adobe PDF".</li>
                        <li>Click "Save" or "Print". This will open your file explorer and let you save it to your computer as a PDF.</li>
                    </ol>

                    <div>
                        <strong>PDF Settings:</strong>
                        <ol>
                            <li class="mb-1">
                                <span>Show/Hide Logo</span>
                                <select v-model="showLogo">
                                    <option :value="true">Show Logo</option>
                                    <option :value="false">Hide Logo</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Admin Section</span>
                                <select v-model="showAdmin">
                                    <option :value="1">Show Admin Section</option>
                                    <option :value="0">Hide Admin Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Jobsite Section</span>
                                <select v-model="showJobsite">
                                    <option :value="1">Show Jobsite Section</option>
                                    <option :value="0">Hide Jobsite Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Equipment Section</span>
                                <select v-model="showEquipment">
                                    <option :value="1">Show Equipment Section</option>
                                    <option :value="0">Hide Equipment Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Employee Section</span>
                                <select v-model="showEmployee">
                                    <option :value="1">Show Employee Section</option>
                                    <option :value="0">Hide Employee Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Crew Section</span>
                                <select v-model="showCrew">
                                    <option :value="1">Show Crew Section</option>
                                    <option :value="0">Hide Crew Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Root Cause Analysis Section</span>
                                <select v-model="showRootCause">
                                    <option :value="1">Show Root Cause Analysis Section</option>
                                    <option :value="0">Hide Root Cause Analysis Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Investigation Participants Section</span>
                                <select v-model="showInvestiPart">
                                    <option :value="1">Show Investigation Participants Section</option>
                                    <option :value="0">Hide Investigation Participants Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Type of Work Section</span>
                                <select v-model="showTypeOfWork">
                                    <option :value="1">Show Type of Work Section</option>
                                    <option :value="0">Hide Type of Work Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Detailed Description Section</span>
                                <select v-model="showDetails">
                                    <option :value="1">Show Detailed Description Section</option>
                                    <option :value="0">Hide Detailed Description Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Corrective Actions Section</span>
                                <select v-model="showCorrectiveActions">
                                    <option :value="1">Show Corrective Actions Section</option>
                                    <option :value="0">Hide Corrective Actions Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Injury/Medical Section</span>
                                <select v-model="showInjury">
                                    <option :value="1">Show Injury/Medical Section</option>
                                    <option :value="0">Hide Injury/Medical Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Additional Section</span>
                                <select v-model="showAdditional">
                                    <option :value="1">Show Additional Section</option>
                                    <option :value="0">Hide Additional Section</option>
                                </select>
                            </li>
                            <li class="mb-1">
                                <span>Show/Hide Files/Photos Section</span>
                                <select v-model="showFilesAndPhotos">
                                    <option :value="1">Show Files/Photos Section</option>
                                    <option :value="0">Hide Files/Photos Section</option>
                                </select>
                            </li>
                        </ol>
                    </div>
                </div>
            </div>

            <div class="container bg-white py-4">

                <div class="row" v-if="pdfLogo && showLogo === true" style="margin-bottom: 50px;">
                    <div class="col-3">
                        <img :src="pdfLogo" class="img-fluid" />
                    </div>
                </div>

                <h5>Incident Details</h5>
                <div class="row">
                    <div class="col-6">
                        <div class="row mb-2">
                            <div class="col-5 text-muted">Status</div>
                            <div class="col-7"><h5 :class="`text-${incident.status_color}`">{{ incident.status_name }}</h5></div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-5 text-muted">Incident Date</div>
                            <div class="col-7">{{ incident.IncidentDate | moment("MM/DD/YYYY") }}</div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-5 text-muted">Type of Claim</div>
                            <div class="col-7">{{ incident.IncidentTypeName }}</div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-5 text-muted">Employee</div>
                            <div class="col-7">
                                <router-link :to="`/employees/view/${incident.EmployeeId}`" v-if="incident.EmployeeId">{{ incident.EmployeeFirstName }} {{ incident.EmployeeLastName }}</router-link>
                                <span v-else>{{ incident.TempEmployeeName }}</span>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-5 text-muted">Exact Location of Incident</div>
                            <div class="col-7">{{ incident.IncidentLocation }}</div>
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="row mb-2">
                            <div class="col-7 text-muted">Is Incident Time Undertermined?</div>
                            <div class="col-5">
                                <span v-if="incident.IsIncidentTimeUndetermined">Yes</span>
                                <span v-else>No</span>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-7 text-muted">Time of Incident</div>
                            <div class="col-5">{{ incident.IncidentTime | time }}</div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-7 text-muted">Date Employee Reported Incident to Supervisor</div>
                            <div class="col-5">{{ incident.IncidentReportedDate | moment("MM/DD/YYYY") }}</div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-7 text-muted">Time Work Began</div>
                            <div class="col-5">{{ incident.BeginWorkTime | time }}</div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-7 text-muted">Did Incident Occur Due to Rework?</div>
                            <div class="col-5">
                                <label class="text-success" v-if="incident.due_to_rework">Yes</label>
                                <label class="text-danger" v-else>No</label>
                            </div>
                        </div>
                    </div>
                </div>
                <hr/>

                <div class="mb-3">
                    <span class="text-muted">Incident Witness:</span> {{ incident.IncidentWitness }}
                </div>

                <div class="mb-2">
                    <span class="text-muted">Was a PTP Completed for the Work Being Performed?</span>
                    <label class="text-success" v-if="incident.IsPtpCompleted == 1">Yes</label>
                    <label class="text-danger" v-else-if="incident.IsPtpCompleted == 0">No</label>
                    <label class="text-default" v-else>N/A</label>
                </div>
                <div class="mb-2">
                    <span class="text-muted">Was the PTP Thorough and Identified All Hazards and Corrective Measures for the Task?</span>
                    <label class="text-success" v-if="incident.IsPtpThorough == 1">Yes</label>
                    <label class="text-danger" v-else-if="incident.IsPtpThorough == 0">No</label>
                    <label class="text-default" v-else>N/A</label>
                </div>

                <incident-sections-view :sections="sections" :pdfView="true" baseSection="incident" labelSize="col-3 text-muted" fieldSize="col-9"></incident-sections-view>

                <div class="row">
                    <div class="col-6" v-if="showJobsite">
                        <h5 class="mt-4 mb-2">
                            <template v-if="incident.JobsiteId">
                                Jobsite
                            </template>
                            <template v-else>
                                Location
                            </template>
                        </h5>
                        <template v-if="incident.JobsiteId">
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Jobsite Number:</div>
                                <div class="col-8">
                                    {{ incident.JobsiteNumber }}
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Jobsite Name:</div>
                                <div class="col-8">
                                    {{ incident.JobsiteName }}
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Division:</div>
                                <div class="col-8">
                                    {{ incident.JobsiteDivision }}
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Address:</div>
                                <div class="col-8">
                                    {{ incident.JobsiteAddress }} {{ incident.JobsiteAddress2 }}<br/>
                                    {{ incident.JobsiteCity }}, {{ incident.JobsiteState }} {{ incident.JobsiteZip }}
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Other Location:</div>
                                <div class="col-8">
                                    {{ incident.other_location }}
                                </div>
                            </div>
                        </template>

                        <incident-sections-view :sections="sections" :pdfView="true" baseSection="jobsite" labelSize="col-4 text-muted" fieldSize="col-8"></incident-sections-view>
                    </div>
                    <div class="col-6" v-if="showEquipment">

                        <h5 class="mt-4 mb-2">Equipment</h5>
                        <template v-if="incident.equipment_id">
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Type</div>
                                <div class="col-8">{{ incident.equipment_type_name }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Model#</div>
                                <div class="col-8">{{ incident.model_number }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Serial#</div>
                                <div class="col-8">
                                    <router-link :to="`/equipment/view/${incident.equipment_id}`">
                                        {{ incident.serial_number }}
                                    </router-link>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Name</div>
                                <div class="col-8">{{ incident.equipment_name }}</div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Equipment</div>
                                <div class="col-8">{{ incident.Equipment }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Equipment Make</div>
                                <div class="col-8">{{ incident.EquipmentMake }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Equipment Model#</div>
                                <div class="col-8">{{ incident.EquipmentModel }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Equipment Serial#</div>
                                <div class="col-8">{{ incident.EquipmentSerial }}</div>
                            </div>
                        </template>

                        <template v-if="incident.additional_equipment.length > 0">
                            <h5 class="mt-4 mb-2">Additional Equipment ({{ incident.additional_equipment.length }})</h5>

                            <div class="row mb-2" v-for="equip in incident.additional_equipment">
                                <div class="col-12">
                                    {{ equip.equipment_type_name + ': ' + equip.serial_number }}
                                    <template v-if="equip.model_number">
                                        - {{ equip.model_number }}
                                    </template>
                                </div>
                            </div>
                        </template>

                        <incident-sections-view :sections="sections" :pdfView="true" baseSection="equipment" labelSize="col-4 text-muted" fieldSize="col-8"></incident-sections-view>
                    </div>
                </div>

                <template v-if="showEmployee">
                    <h5 class="mt-4 mb-2">Employee</h5>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Name</div>
                        <div class="col-5">
                            <span v-if="incident.EmployeeId">{{ incident.EmployeeFirstName }} {{ incident.EmployeeLastName }}</span>
                            <span v-else>{{ incident.TempEmployeeName }}</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Job Title</div>
                        <div class="col-5">
                            {{ incident.EmployeeTitle }}
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Is Temporary Employee</div>
                        <div class="col-5">
                            <span v-if="incident.IsTempEmployee">Yes</span>
                            <span v-else>No</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Is Contractor</div>
                        <div class="col-5">
                            <span v-if="incident.IsContractor">Yes</span>
                            <span v-else>No</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Subcontractor</div>
                        <div class="col-5">
                            {{ incident.ContractorCompany }}
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Phone Number</div>
                        <div class="col-5">
                            {{ incident.EmployeePhone }}
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Address</div>
                        <div class="col-5">
                            {{ incident.EmployeeAddress }} {{ incident.EmployeeAddress2 }}<br/>
                            {{ incident.EmployeeCity }}, {{ incident.EmployeeState }} {{ incident.EmployeeZip }}
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Additional Employees:</div>
                        <div class="col-5">
                            <ul class="list-unstyled">
                                <li v-for="emp in incident.additional_employees">
                                    {{ emp.FirstName + ' ' + emp.LastName }}
                                    <template v-if="emp.EmployeeNumber">
                                        - {{ emp.EmployeeNumber }}
                                    </template>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <incident-sections-view :sections="sections" :pdfView="true" baseSection="employee" labelSize="col-4 text-muted" fieldSize="col-5"></incident-sections-view>
                </template>

                <template v-if="showCrew">
                    <h5 class="mt-4 mb-2">Crew</h5>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Foreman</div>
                        <div class="col-8">
                            {{ incident.foreman_name }}
                            <span class="text-info" title="Legacy Value" v-if="incident.Foreman">({{ incident.Foreman }})</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Leadman</div>
                        <div class="col-8">
                            {{ incident.leadman_name }}
                            <span class="text-info" title="Legacy Value" v-if="incident.Leadman">({{ incident.Leadman }})</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Superintendent</div>
                        <div class="col-8">
                            <user-name :user="superintendent" :plainText="true" />
                            <span class="text-info" title="Legacy Value" v-if="incident.Superintendent">({{ incident.Superintendent }})</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Project Manager</div>
                        <div class="col-8">
                            <user-name :user="project_manager" :plainText="true" />
                            <span class="text-info" title="Legacy Value" v-if="incident.ProjectManager">({{ incident.ProjectManager }})</span>
                        </div>
                    </div>
                    <div class="row mb-2" v-if="incident.inci_require_supervisor == 1">
                        <div class="col-4 text-muted">Supervisor</div>
                        <div class="col-8">
                            <user-name :user="supervisor" :plainText="true" v-if="incident.supervisor_id" />
                            <span v-else-if="incident.other_supervisor_name">{{ incident.other_supervisor_name }}</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-4 text-muted">Crew Size</div>
                        <div class="col-8">
                            {{ incident.CrewSize }}
                        </div>
                    </div>
                    <div class="mb-2">
                        <div class="text-muted">Crew Members:</div>
                        <div v-html="incident.CrewMembers"></div>
                    </div>

                    <incident-sections-view :sections="sections" :pdfView="true"baseSection="crew_members" labelSize="col-4 text-muted" fieldSize="col-8"></incident-sections-view>
                </template>

                <template v-if="showRootCause">
                    <div class="break-protect">
                        <h5 class="mt-4 mb-2">Root Cause Analysis</h5>
                        <div class="row mb-2">
                            <div class="col-4 text-muted">Hazards:</div>
                            <div class="col-8">
                                <span class="text-default mr-1" v-for="(hazard, index) in incident.hazards"><template v-if="index > 0">, </template>{{ hazard.hazard_name }}</span>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-4 text-muted">Contributing Behaviors:</div>
                            <div class="col-8">
                                <span class="text-default mr-1" v-for="(behavior, index) in incident.behaviors"><template v-if="index > 0">, </template>{{ behavior.behavior_name }}</span>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-4 text-muted">Contributing Conditions:</div>
                            <div class="col-8">
                                <span class="text-default mr-1" v-for="(condition, index) in incident.conditions"><template v-if="index > 0">, </template>{{ condition.condition_name }}</span>
                            </div>
                        </div>

                        <incident-sections-view :sections="sections" :pdfView="true"baseSection="root_cause" labelSize="col-4 text-muted" fieldSize="col-8"></incident-sections-view>
                    </div>
                    <hr/>
                </template>

                <div class="break-protect">
                    <div class="mb-2">
                        <div class="text-muted">Unsafe Act(s)/Work Practice Contributing to Incident</div>
                        <div v-html="incident.UnsafeActOrPractice"></div>
                    </div>
                </div>
                <div class="break-protect">
                    <div class="mb-2">
                        <div class="text-muted mt-3">Unsafe Condition (If Applicable)</div>
                        <div v-html="incident.UnsafeCondition"></div>
                    </div>
                </div>
                <hr/>

                <template v-if="showInvestiPart">
                    <div class="break-protect mt-4">
                        <h5 class="mt-4 mb-2">Investigation Participants</h5>
                        <div v-html="incident.incident_participants"></div>
                        <hr/>

                        <incident-sections-view :sections="sections" :pdfView="true"baseSection="participants" labelSize="col-3 text-muted" fieldSize="col-9"></incident-sections-view>
                    </div>
                </template>

                <template v-if="showTypeOfWork">
                    <div class="break-protect mt-4">
                        <h5 class="mt-4 mb-2">Type of Work Being Performed At Time of Incident</h5>
                        <div v-html="incident.TypeOfWorkPerformed"></div>
                        <hr/>

                        <incident-sections-view :sections="sections" :pdfView="true" baseSection="type_of_work" labelSize="col-3 text-muted" fieldSize="col-9"></incident-sections-view>
                    </div>
                </template>

                <template v-if="showDetails">
                    <div class="break-protect mt-4">
                        <h5 class="mt-4 mb-2">Detailed Description of the Incident</h5>
                        <div v-html="incident.DetailedDescription"></div>
                        <hr/>

                        <incident-sections-view :sections="sections" :pdfView="true" baseSection="description" labelSize="col-3 text-muted" fieldSize="col-9"></incident-sections-view>
                    </div>
                </template>

                <template v-if="showCorrectiveActions">
                    <h5 class="mt-4 mb-2">Corrective Actions ({{ actions.length }})</h5>
                    <hr/>
                    <div v-for="action in actions" class="my-2">
                        <div class="row">
                            <div class="col-6">
                                <div class="d-print-none mb-2">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Status:</span>
                                    <span :class="`text-${action.status_color}`">{{ action.status_name }}</span>
                                </div>
                                <div class="d-none d-print-block mb-2">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Status:</span>
                                    <span :class="`text-${action.status_color}`">{{ action.status_name }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Source:</span>
                                    <span>{{ action.source_name }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Location:</span>
                                    <span>{{ action.location }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Created By:</span>
                                    <span>{{ action.creator_first_name }} {{ action.creator_last_name }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Assigned To:</span>
                                    <span>{{ action.assignee_first_name }} {{ action.assignee_last_name }}</span>
                                </div>
                            </div>
                            <div class="col-6">
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Subcontractor:</span>
                                    <span>{{ action.subcontractor }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Completed By:</span>
                                    <span>{{ action.completer_first_name }} {{ action.completer_last_name }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Created Date:</span>
                                    <span>{{ action.created_at_formatted }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Due Date:</span>
                                    <span>{{ action.due_date | moment("MM/DD/YYYY") }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Completed Date:</span>
                                    <span>{{ action.completed_at_formatted }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="text-muted d-sm-inline-block d-xs-block">Time to Completion:</span>
                                    <span>{{ action.time_to_completion }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="mb-2">
                            <label class="text-muted">Details:</label><br>
                            {{ action.detail }}
                        </div>
                        <div>
                            <label class="text-muted">Completed Notes:</label><br>
                            {{ action.completed_detail }}
                        </div>
                        <hr/>
                    </div>

                    <incident-sections-view :sections="sections" :pdfView="true" baseSection="actions" labelSize="col-3 text-muted" fieldSize="col-9"></incident-sections-view>
                </template>

                <template v-if="showInjury">
                    <h5 class="mt-4 mb-2">Injury/Medical Information</h5>
                    <div class="row">
                        <div class="col-5">
                            <div class="row mb-2">
                                <div class="col-4 text-muted">What Object or Substance Directly Harmed Employee?</div>
                                <div class="col-8">{{ incident.InjuryObjectOrSubstance }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Medical Professional</div>
                                <div class="col-8">{{ incident.MedicalProfessional }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Did Employee Return to Work?</div>
                                <div class="col-8">
                                    <template v-if="incident.IsReturnToWork == 1">Yes</template>
                                    <template v-if="incident.IsReturnToWork == 0">No</template>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Date Returned to Work</div>
                                <div class="col-8">{{ incident.ReturnToWorkDate | moment("MM/DD/YYYY") }}</div>
                            </div>
                        </div>
                        <div class="col-7">
                            <h6 class="mt-4 mb-2">Treatment Facility</h6>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Place of Treatment</div>
                                <div class="col-8">{{ incident.FacilityTypeName }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Facility Name</div>
                                <div class="col-8">{{ incident.FacilityName }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Facility Address</div>
                                <div class="col-8">
                                    {{ incident.FacilityAddress }} {{ incident.FacilityAddress2 }}<br/>
                                    {{ incident.FacilityCity }}<span v-if="incident.FacilityCity && incident.FacilityState">,</span> {{ incident.FacilityState }} {{ incident.FacilityZip }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <incident-sections-view :sections="sections" :pdfView="true"baseSection="injury" labelSize="col-3 text-muted" fieldSize="col-9"></incident-sections-view>
                </template>

                <template v-if="showAdditional">
                    <h5 class="mt-4 mb-2">Additional Information</h5>
                    <div class="row mb-2">
                        <div class="col-3 text-muted">Report Completed By:</div>
                        <div class="col-9">{{ incident.user_name }} <span v-if="incident.CreatedByTitle"> | {{ incident.CreatedByTitle }}</span></div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-3 text-muted">Report Created Date:</div>
                        <div class="col-9">{{ incident.created_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</div>
                    </div>

                    <incident-sections-view :sections="sections" :pdfView="true"baseSection="additional" labelSize="col-3 text-muted" fieldSize="col-9"></incident-sections-view>
                    <hr/>

                    <template v-if="incident.AdditionalInformation">
                        <div v-html="incident.AdditionalInformation"></div>
                    </template>
                    <template v-else>
                        --
                    </template>
                </template>
                <hr/>

                <template v-if="showAdmin && this.$root.hasPermission('incidents.manage')">
                    <h5 class="mt-4 mb-2">Administrative</h5>
                    <div class="row">
                        <div class="col-6">
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Employee#:</div>
                                <div class="col-7">{{ incident.EmployeeNumber }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Employee Date of Birth:</div>
                                <div class="col-7">{{ incident.BirthDate | moment("MM/DD/YYYY") }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Employee Date Hired:</div>
                                <div class="col-7">{{ incident.HireDate | moment("MM/DD/YYYY") }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Employee Weekly Salary:</div>
                                <div class="col-7">{{ incident.WeeklySalary | currency }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Major Body Part:</div>
                                <div class="col-7">{{ incident.MajorBodyPartName }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Specific Body Part:</div>
                                <div class="col-7">{{ incident.SpecificBodyPartName }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Nature of Injury:</div>
                                <div class="col-7">{{ incident.InjuryTypeName }}</div>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="row mb-2">
                                <div class="col-4 text-muted">OSHA Recordable?:</div>
                                <div class="col-7">
                                    <span v-if="incident.IsOshaRecordable">Yes</span>
                                    <span v-else>No</span>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">OSHA Case#:</div>
                                <div class="col-7">{{ incident.OshaCaseNumber }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">OSHA Injury Type:</div>
                                <div class="col-7">{{ incident.OshaInjuryTypeName }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">OSHA Most Serious Outcome:</div>
                                <div class="col-7">{{ incident.OshaOutcomeTypeName }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Days Away from Work:</div>
                                <div class="col-7">{{ incident.AwayFromWorkDays }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Days on Job Transfer Or Restriction:</div>
                                <div class="col-7">{{ incident.JobTransferOrRestrictionDays }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">Date of Death:</div>
                                <div class="col-7">{{ incident.DeathDate | moment("MM/DD/YYYY") }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4 text-muted">DOT Reportable?:</div>
                                <div class="col-7">
                                    <span v-if="incident.IsDotRecordable">Yes</span>
                                    <span v-else>No</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr/>
                </template>

                <div v-for="section in sections" class="mt-2 mb-1">

                    <template v-if="!section.base_section">
                        <h5 v-if="section.section_name">{{ section.section_name }}</h5>

                        <template v-for="field in section.fields">
                            <template v-if="field.name == 'Heading' || field.name == 'Paragraph'">
                                <!-- Do nothing for Heading and Paragraph Fields  -->
                            </template>
                            <div class="row mb-2" v-else-if="field.parent_field_id == null || (field.parent_field_id != null && field.value)">
                                <div class="col-4 text-muted">{{ field.label }}:</div>
                                <div class="col-7">
                                    <template v-if="field.name == 'Textarea'">
                                        <div v-html="field.value"></div>
                                    </template>
                                    <template v-else-if="Array.isArray(field.value)" v-for="(val, key) in field.value">
                                        <span v-if="key > 0">, </span>{{ val }}
                                    </template>
                                    <template v-else>{{ field.value }}</template>
                                </div>
                            </div>
                        </template>
                        <hr/>
                    </template>

                </div>

                <div style="page-break-after: always;"></div>

                <template v-if="showFilesAndPhotos">
                    <h5 class="mt-4">
                        Files({{ nonImages.length }}) &amp; Photos({{ images.length }})
                        <button class="btn btn-sm btn-warning" title="PDFs are processing. Click here to refresh." v-on:click="reloadImages()" :disabled="imagesLoading" v-if="pdfs_proccessing">
                            <i class="fa fa-refresh" :class="{'fa-spin': imagesLoading}"></i> PDFs
                        </button>
                    </h5>

                    <incident-sections-view :sections="sections" :pdfView="true" baseSection="files" labelSize="col-3 text-muted" fieldSize="col-9"></incident-sections-view>

                    <div class="row mt-4">
                        <div class="col-2" v-for="file in nonImages">
                            <img v-bind:src="getThumbnail(file)" class="img-fluid" :ref="`file-${file.file_id}`">
                            <div class="text-muted small">
                                <strong class="project-attachment-filename" style="color: #333;">{{ file.file_name }}</strong><br/>
                                {{ file.created_at | moment("timezone", userTimezone, "MM/DD/YYYY hh:mm A") }} | {{ file.FirstName }} {{ file.LastName }}
                            </div>
                            <hr class="d-print-none" />
                        </div>
                    </div>
                    <hr/>
                    <template v-if="nonImages.length > 0 && images.length > 0">
                        <p>Images are attached are the next pages</p>
                        <div style="page-break-after: always;"></div>
                    </template>

                    <div class="text-center" style="page-break-after: always;" v-for="file in images">
                        <img v-bind:src="getThumbnail(file)" class="img-fluid" :ref="`file-${file.file_id}`">
                        <div class="text-muted small">
                            <strong class="project-attachment-filename" style="color: #333;">{{ file.file_name }}</strong><br/>
                            {{ file.created_at | moment("timezone", userTimezone, "MM/DD/YYYY hh:mm A") }} | {{ file.FirstName }} {{ file.LastName }}
                        </div>
                        <hr class="d-print-none" />
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {
        data: function() {
            return {
                userTimezone: this.$store.state.user.timezone,
                loading: true,
                IncidentId: this.$route.params.id,
                incident: {},
                actions: [],
                files: [],
                emailLink: '',
                sections: [],
                showLogo: true,
                showAdmin: 0,
                showJobsite: 1,
                showEquipment: 1,
                showEmployee: 1,
                showCrew: 1,
                showRootCause: 1,
                showInvestiPart: 1,
                showTypeOfWork: 1,
                showDetails: 1,
                showCorrectiveActions: 1,
                showInjury: 1,
                showAdditional: 1,
                showFilesAndPhotos: 1,
                pdfLogo: null,
                imagesLoading: false,
                pdfs_proccessing: false,
            }
        },

        computed: {
            superintendent() {
                return {
                    FirstName: this.incident.SuperintendentFirstName,
                    LastName: this.incident.SuperintendentLastName,
                    deleted_at: this.incident.SuperintendentDeletedAt,
                };
            },

            project_manager() {
                return {
                    FirstName: this.incident.ProjectManagerFirstName,
                    LastName: this.incident.ProjectManagerLastName,
                    deleted_at: this.incident.ProjectManagerDeletedAt,
                };
            },

            supervisor() {
                return {
                    FirstName: this.incident.SupervisorFirstName,
                    LastName: this.incident.SupervisorLastName,
                    deleted_at: this.incident.SupervisorDeletedAt,
                };
            },

            incidentDateFormatted(){
                //Format it
                return moment(this.incident.IncidentDate, 'YYYY-MM-DD').format('MM/DD/YYYY');
            },

            incidentName(){
                //Setup
                var name = '';

                //Add the TypeName
                name += (this.incident.IncidentTypeName ? this.incident.IncidentTypeName : 'Incident') + '-';

                //Add the Date
                name += this.incidentDateFormatted + '-';

                //Add the Jobsite
                name += this.incident.JobsiteName;

                //Send it
                return name;
            },

            images(){
                //Make sure we only pass images
                var types = ['jpg', 'jpeg', 'png', 'gif', 'tiff'];

                //Setup
                var imageFiles = [];

                //loop
                for(var i in this.files)
                {
                    if(types.indexOf(this.files[i].file_extension.toLowerCase()) !== -1)
                    {
                        imageFiles.push(this.files[i]);
                    }
                }

                //Send it
                return imageFiles;
            },

            nonImages(){
                //Make sure we only pass images
                var types = ['jpg', 'jpeg', 'png', 'gif', 'tiff'];

                //Setup
                var files = [];

                //loop
                for(var i in this.files)
                {
                    if(types.indexOf(this.files[i].file_extension.toLowerCase()) === -1)
                    {
                        files.push(this.files[i]);
                    }
                }

                //Send it
                return files;
            }
        },

        methods: {

            //Get the thumbnail preview
            getThumbnail(file){
                var preview = null;

                //Switch
                switch(file.file_extension)
                {
                    case 'pdf':
                        preview = '/Images/file-type-pdf_256x256.png';
                        break;
                    case 'docx':
                    case 'doc':
                        preview = '/Images/file-type-docx_256x256.png';
                        break;
                    case 'xls':
                    case 'xlsx':
                        preview = '/Images/file-type-xlsx_256x256.png';
                        break;
                    default:
                        //Data should be a path, not a string - we converted those
                        // $path = $this->{$imageDataName};
                        // $type = pathinfo($path, PATHINFO_EXTENSION);
                        // $data = Storage::get($path);
                        // $preview = "data:image/".$type.";base64," . base64_encode($data);
                        this.loadThumbnail(file);

                        //Stop here
                        return '';
                        break;
                }

                //Stop here
                return preview;

                //Set the preview
                //this.$refs['file-' + file.file_id][0].src = preview;
            },

            loadThumbnail(file){
                //Send it
                this.$http.get('files/view/'+file.file_id, {display_type: 'fluid'})
                    .then(response => {
                        //Sent the ref
                        this.$refs['file-' + file.file_id][0].src = response.data;
                    })
                    .catch(err => {
                        switch(err.status)
                        {
                            case 410:
                                //Stop here?
                                //this.$refs['file-' + file.file_id][0].src = '/Images/file-not-found.png';
                                break;
                        }
                    });
            },

            incidentPartsUpdated: function(data){
                //Update the cost
                this.incident.cost = data.incident_cost;

                //Redraw the table
                this.$refs.incidentParts.refreshData();
            },

            getIncident() {
                //Get the data
                this.$http
                    .get('incident/view/' + this.IncidentId + '?pdf=true')
                    .then(response => {
                        //Set all the data
                        this.incident = response.data.incident;
                        this.actions = response.data.actions;
                        this.files = response.data.files;
                        this.emailLink = response.data.emailLink;
                        this.sections = response.data.sections;
                        this.pdfLogo = response.data.pdfLogo;
                        this.pdfs_proccessing = response.data.pdfs_proccessing;

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

            reloadImages(){
                //Reset the flag
                this.imagesLoading = true;

                //Send it
                this.$http
                    .get('incident/images/' + this.IncidentId)
                    .then(response => {
                        //Set the data
                        this.files = response.data.images;
                        this.pdfs_proccessing = response.data.pdfs_proccessing;

                        //Alert
                        this.$toastr.success('Files & Photos Reloaded');
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Reset the flag
                        this.imagesLoading = false;
                    });
            }

        },
        mounted() {
            //Get the incidents
            this.getIncident();
        }
    }
</script>
<style>
    .break-protect {
        break-inside: auto;
    }
    .body {
        margin: 25mm;
    }
    @page {
        margin-top: 1in;
        margin-bottom: 1in;
    }
</style>